import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Tooltip, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Snackbar, Alert, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Accordion, AccordionSummary, AccordionDetails, Button } from '@mui/material'; // Import Button
import { Add, ExpandMore, Save, Delete, AttachFile } from '@mui/icons-material'; // Import icons
import axios from 'axios'; // Import axios for API calls
import config from '../config/config'; // Import configuration
import MilestoneEditPopup from './MilestoneEditPopup'; // Import MilestoneEditPopup
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ImageAttachmentDialog from './ImageAttachmentDialog'; // Import ImageAttachmentDialog
import { localeMap } from '../utils/formatterUtils'; // Import localeMap

const MilestoneTreeView = ({
  milestones,
  tasks = {}, // Ensure tasks is an object
  projectId, // Add projectId as a prop
  handleTaskChange,
  handleAddTask,
  handleDeleteTask,
  handleMilestoneChange,
  handleSaveMilestone,
  handleDeleteMilestone,
  fetchTasks, 
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [milestoneList, setMilestoneList] = useState(milestones);

  useEffect(() => {
    setMilestoneList(milestones);
  }, [milestones]);

  const handleDialogClose = () => {
    setDialogOpen(false);
    setCurrentMilestone(null);
  };

  const handleAttachmentDialogOpen = (taskId) => {
    setCurrentTaskId(taskId);
    setAttachmentDialogOpen(true);
  };

  const handleAttachmentDialogClose = () => {
    setAttachmentDialogOpen(false);
    setCurrentTaskId(null);
  };

  const validateDates = (startDate, endDate) => {
    if (startDate && endDate) {
      return new Date(startDate) <= new Date(endDate);
    }
    return true;
  };

  const handleSaveTask = async (task, milestoneId) => {
    try {
      const token = localStorage.getItem('authToken');
      const taskPayload = {
        name: task.name,
        description: task.description,
        project: { project_id: projectId },
        milestone: { id: milestoneId },
        status: task.status,
        priority: task.priority,
        startDate: task.startDate,
        endDate: task.endDate,
        assignedTo: task.assignedTo,
      };

      if (!validateDates(task.startDate, task.endDate)) {
        setSnackbarMessage('Start date must be the same or before the end date.');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        return;
      }

      let response;
      if (!task.id || (typeof task.id === 'string' && task.id.startsWith('new-'))) {
        // New task
        response = await axios.post(`${config.apiBaseUrl}/api/tasks`, taskPayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
      } else {
        // Existing task
        response = await axios.put(`${config.apiBaseUrl}/api/tasks/${task.id}`, taskPayload, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-TenantID': 'admin',
          },
        });
      }

      if (response.status === 201 || response.status === 200) {
        setSnackbarMessage('Task saved successfully!');
        setSnackbarSeverity('success');
      } else {
        setSnackbarMessage('Failed to save task!');
        setSnackbarSeverity('error');
      }
      fetchTasks(milestoneId); // Refresh tasks after saving
    } catch (error) {
      if (error.response) {
        setSnackbarMessage(`Error saving task: ${error.response.data.message || error.response.statusText}`);
      } else {
        setSnackbarMessage('Error saving task!');
      }
      setSnackbarSeverity('error');
    } finally {
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleDateChange = (date, name, taskId, milestoneId) => {
    const formattedDate = date ? date.toISOString().split('T')[0] : '';
    const task = tasks[milestoneId].find((t) => t.id === taskId);
    if (name === 'startDate' && !validateDates(formattedDate, task.endDate)) {
      setSnackbarMessage('Start date must be the same or before the end date.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (name === 'endDate' && !validateDates(task.startDate, formattedDate)) {
      setSnackbarMessage('End date must be the same or after the start date.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    handleTaskChange({ target: { name, value: formattedDate } }, taskId, milestoneId);
    setIsDirty(true);
  };

  const handleMilestoneDateChange = (date, name, milestoneId) => {
    const formattedDate = date ? date.toISOString().split('T')[0] : '';
    const milestone = milestones.find((m) => m.id === milestoneId);
    if (name === 'startDate' && !validateDates(formattedDate, milestone.endDate)) {
      setSnackbarMessage('Start date must be the same or before the end date.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    if (name === 'endDate' && !validateDates(milestone.startDate, formattedDate)) {
      setSnackbarMessage('End date must be the same or after the start date.');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    handleMilestoneChange({ target: { name, value: formattedDate } }, milestoneId);
    setIsDirty(true);
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false);
  };

  const handleConfirmSave = () => {
    if (currentMilestone) {
      handleSaveMilestone(currentMilestone);
    }
    setConfirmDialogOpen(false);
    // Switch view logic here
  };

  // Sort milestones by startDate
  const sortedMilestones = [...milestoneList].sort((a, b) => new Date(a.startDate) - new Date(b.startDate));

  return (
    <Box>
      {sortedMilestones.map((milestone) => (
        <Accordion key={milestone.id} sx={{ backgroundColor: '#f7f7f7' }}>
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Typography>{milestone.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Paper elevation={3} style={{ padding: '16px' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <TextField
                  label="Milestone Name"
                  name="name"
                  value={milestone.name}
                  onChange={(e) => {
                    handleMilestoneChange(e, milestone.id);
                    setIsDirty(true);
                  }}
                  sx={{ flexGrow: 1, mr: 2 }}
                />
                <Box>
                  <Button variant="text" color="primary" onClick={() => handleSaveMilestone(milestone)}>
                    Save Milestone
                  </Button>
                  <Button variant="text" color="secondary" onClick={() => handleDeleteMilestone(milestone.id)} sx={{ ml: 1 }}>
                    Delete Milestone
                  </Button>
                </Box>
              </Box>
              <TextField
                label="Milestone Description"
                name="description"
                value={milestone.description}
                onChange={(e) => {
                  handleMilestoneChange(e, milestone.id);
                  setIsDirty(true);
                }}
                fullWidth
                multiline
                sx={{ mt: 2 }}
              />
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={2}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                  <DatePicker
                    label="Start Date"
                    value={milestone.startDate ? new Date(milestone.startDate) : null}
                    onChange={(date) => handleMilestoneDateChange(date, 'startDate', milestone.id)}
                    renderInput={(params) => <TextField {...params} fullWidth InputProps={{ readOnly: true }} />}
                    inputFormat="yyyy-MM-dd"
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                  <DatePicker
                    label="End Date"
                    value={milestone.endDate ? new Date(milestone.endDate) : null}
                    onChange={(date) => handleMilestoneDateChange(date, 'endDate', milestone.id)}
                    renderInput={(params) => <TextField {...params} fullWidth InputProps={{ readOnly: true }} />}
                    inputFormat="yyyy-MM-dd"
                  />
                </LocalizationProvider>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                  <DatePicker
                    label="Due Date"
                    value={milestone.dueDate ? new Date(milestone.dueDate) : null}
                    onChange={(date) => handleMilestoneDateChange(date, 'dueDate', milestone.id)}
                    renderInput={(params) => <TextField {...params} fullWidth InputProps={{ readOnly: true }} />}
                    inputFormat="yyyy-MM-dd"
                  />
                </LocalizationProvider>
                <TextField
                  label="Status"
                  name="status"
                  value={milestone.status}
                  onChange={(e) => {
                    handleMilestoneChange(e, milestone.id);
                    setIsDirty(true);
                  }}
                  sx={{ flexGrow: 1, ml: 2 }}
                />
              </Box>
              <Box mt={2} border={1} borderColor="grey.400" borderRadius={4} padding={2}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box display="flex" alignItems="center">
                    <Tooltip title="Tasks">
                      <Typography variant="subtitle1">Tasks</Typography>
                    </Tooltip>
                    <Tooltip title="Add Task">
                      <IconButton color="primary" onClick={() => handleAddTask(milestone.id)} sx={{ ml: 1 }}>
                        <Add />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Task Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Assigned To</TableCell>
                        <TableCell>Start Date</TableCell>
                        <TableCell>End Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {tasks[milestone.id] && tasks[milestone.id].length > 0 ? (
                        tasks[milestone.id].map((task) => (
                          <TableRow key={task.id || `new-${Date.now()}`}>
                            <TableCell>
                              <TextField
                                label="Task Name"
                                name="name"
                                value={task.name || ''}
                                onChange={(e) => {
                                  handleTaskChange(e, task.id, milestone.id);
                                  setIsDirty(true);
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                label="Status"
                                name="status"
                                value={task.status || ''}
                                onChange={(e) => {
                                  handleTaskChange(e, task.id, milestone.id);
                                  setIsDirty(true);
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                label="Assigned To"
                                name="assignedTo"
                                value={task.assignedTo || ''}
                                onChange={(e) => {
                                  handleTaskChange(e, task.id, milestone.id);
                                  setIsDirty(true);
                                }}
                                fullWidth
                              />
                            </TableCell>
                            <TableCell>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                                <DatePicker
                                  label="Start Date"
                                  value={task.startDate ? new Date(task.startDate) : null}
                                  onChange={(date) => handleDateChange(date, 'startDate', task.id, milestone.id)}
                                  renderInput={(params) => <TextField {...params} fullWidth InputProps={{ readOnly: true }} />}
                                  inputFormat="yyyy-MM-dd"
                                />
                              </LocalizationProvider>
                            </TableCell>
                            <TableCell>
                              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={localeMap[config.defaultLocale]}>
                                <DatePicker
                                  label="End Date"
                                  value={task.endDate ? new Date(task.endDate) : null}
                                  onChange={(date) => handleDateChange(date, 'endDate', task.id, milestone.id)}
                                  renderInput={(params) => <TextField {...params} fullWidth InputProps={{ readOnly: true }} />}
                                  inputFormat="yyyy-MM-dd"
                                />
                              </LocalizationProvider>
                            </TableCell>
                            <TableCell>
                              <Tooltip title="Save Task">
                                <IconButton color="primary" onClick={() => handleSaveTask(task, milestone.id)}>
                                  <Save />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Task">
                                <IconButton color="secondary" onClick={() => handleDeleteTask(task.id, milestone.id)}>
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Attach Files">
                                <IconButton color="default" onClick={() => handleAttachmentDialogOpen(task.id)}>
                                  <AttachFile />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography variant="body2" color="textSecondary">
                              No tasks available
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          </AccordionDetails>
        </Accordion>
      ))}
      <MilestoneEditPopup
        dialogOpen={dialogOpen}
        handleDialogClose={handleDialogClose}
        currentMilestone={currentMilestone}
        handleMilestoneChange={handleMilestoneChange}
        tasks={tasks}
        handleTaskChange={handleTaskChange}
        handleAddTask={handleAddTask}
        handleSaveMilestone={handleSaveMilestone}
        fetchTasks={fetchTasks} // Ensure fetchTasks is passed as a prop
      />
      <ImageAttachmentDialog
        open={attachmentDialogOpen}
        onClose={handleAttachmentDialogClose}
        taskId={currentTaskId}
      />
      <Dialog open={confirmDialogOpen} onClose={handleConfirmDialogClose}>
        <DialogTitle>Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have unsaved changes. Do you want to save them before switching views?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmSave} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}>
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MilestoneTreeView;